import { useState } from 'react';
import { isIos } from '../../Utils/dragAndDropUtils';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import {
  ZoomableImageNavigation,
  ZoomableImageHeader,
} from '../ZoomableImage/Components/ZoomableImageToolbar';
import ZoomableImageView from '../ZoomableImage/ZoomableImageView';
import { capitalize } from '../../Utils/stringUtils';
import EkonomiskAnalysDataErrorPage from '../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/ErrorPages/EkonomiskAnalysDataErrorPage/EkonomiskAnalysDataErrorPage';
import { getServerURL } from '../../Utils/getServerURL';
import { getCurrentDateTimeFileName } from '../../Utils/dateUtils';

export interface IDocument {
  url: string;
  extension: string;
  fileName: string;
}

interface IProps {
  documents: IDocument[];
  /** Caption for the thing shown, defaults to "dokument" */
  caption?: string;
  /** Dialog title when viewing an image in full screen */
  imageFullscreenTitle?: string;
  /** If date should be appended to downloaded filename when document is downloaded */
  appendDownloadedDateToFileName?: boolean;
}

const useStyles = makeStyles<IProps, 'navbarContainer'>({
  uniqId: 'documents-styles',
})(
  (theme, _params, classes) =>
    ({
      container: {
        height: '100%',
        position: 'relative',
      },
      img: {
        objectFit: 'contain',
        objectPosition: '50% 50%',
        height: 'inherit',
        width: 'inherit',
      },
      pdfContainer: {
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        [`&:hover .${classes.navbarContainer}`]: {
          height: '30px',
          opacity: 0.5,
        },
      },
      pdf: {
        height: '100%',
        width: '100%',
      },
      iosErrorContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      iosError: {
        textAlign: 'center',
      },
      iosLink: {},
      navbarContainer: {}, // Add this class to styles
    } as const)
);

export const isImageExtension = (extension: string) => {
  return ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'jfif'].includes(
    extension.toLowerCase()
  );
};

/**
 * Default blob handler url
 */
export const getDocumentUrl = (blobId: string) => {
  return `${getServerURL()}/blob?id=${blobId}`;
};

export const downloadDocument = (
  fileName: string,
  documentUrl: string,
  extension: string,
  appendDownloadedDateToFileName: boolean = false
) => {
  const fileNameWithExtension = `${fileName}${
    appendDownloadedDateToFileName ? ` ${getCurrentDateTimeFileName()}` : ''
  }.${extension}`;

  if ('download' in HTMLAnchorElement.prototype) {
    fetch(documentUrl, {
      method: 'get',
      credentials: 'include',
    }).then(response => {
      if (!response.ok) {
        return;
      }
      response.blob().then(blob => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const untypedNavigator: any = navigator;

        // For internet explorer
        if (untypedNavigator.msSaveBlob || untypedNavigator.msSaveOrOpenBlob) {
          untypedNavigator.msSaveOrOpenBlob(blob, fileNameWithExtension);
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.download = fileNameWithExtension;
          document.body.appendChild(link);
          link.click();
        }
      });
    });
  } else {
    const form = document.createElement('form');
    form.action = `${documentUrl}`;
    form.method = 'get';
    form.target =
      'download' in HTMLAnchorElement.prototype ? '_self' : '_blank';

    const input = document.createElement('input');
    input.name = 'json';
    input.value = JSON.stringify({
      fileName: fileNameWithExtension,
    });
    input.type = 'hidden';

    form.appendChild(input);
    document.body.appendChild(form);

    form.submit();

    document.body.removeChild(form);
  }
};

const Documents: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes, cx } = useStyles(props);
  const [documentIndex, setDocumentIndex] = useState(0);

  const document = props.documents[documentIndex];
  const caption = props.caption ?? 'dokument';

  if (document.extension === '') {
    return null;
  } else if (document.extension.toLowerCase() === 'pdf') {
    if (isIos()) {
      return (
        <div className={cx(classes.pdfContainer, classes.iosErrorContainer)}>
          <EDTypography className={classes.iosError}>
            PDF-{caption} stöds inte att visas direkt på iOS. Klicka{' '}
            <a className={classes.iosLink} target="_blank" href={document.url}>
              här
            </a>{' '}
            för att se pdf filen.
          </EDTypography>
        </div>
      );
    } else {
      return (
        <div className={classes.container}>
          {props.documents.length > 1 && (
            <>
              <ZoomableImageNavigation
                direction="prev"
                imageCount={props.documents.length}
                imageIndex={documentIndex}
                onClick={() => setDocumentIndex(Math.max(documentIndex - 1, 0))}
                header="Underlag"
                headerEttGenus
              />
              <ZoomableImageNavigation
                direction="next"
                imageCount={props.documents.length}
                imageIndex={documentIndex}
                onClick={() =>
                  setDocumentIndex(
                    Math.min(documentIndex + 1, props.documents.length - 1)
                  )
                }
                header="Underlag"
                headerEttGenus
              />
            </>
          )}
          <div className={classes.pdfContainer}>
            <object
              className={classes.pdf}
              type="application/pdf"
              data={document.url}
              data-testid="document-pdf"
            >
              <EDTypography className={classes.iosError}>
                PDF-{caption} stöds inte att visas direkt i din webläsare.
                Klicka{' '}
                <a
                  className={classes.iosLink}
                  target="_blank"
                  href={document.url}
                >
                  här
                </a>{' '}
                för att se pdf filen.
              </EDTypography>
            </object>
          </div>
        </div>
      );
    }
  } else if (isImageExtension(document.extension)) {
    return (
      <div className={classes.container}>
        <ZoomableImageView
          previousImage={() => setDocumentIndex(Math.max(documentIndex - 1, 0))}
          nextImage={() =>
            setDocumentIndex(
              Math.min(documentIndex + 1, props.documents.length - 1)
            )
          }
          imageCount={props.documents.length}
          imageIndex={documentIndex}
          isImage
          title={props.imageFullscreenTitle ?? 'Bild'}
          subtitle={capitalize(caption)}
          noFullscreenToggle={false}
          extraBottomPadding={false}
          navHeader={capitalize(caption)}
          navHeaderEttGenus
          showCenteredImageToggle={false}
          useWheelToScroll={false}
        >
          <img
            className={classes.img}
            src={document.url}
            alt={capitalize(caption)}
            data-testid="document-image"
          />
        </ZoomableImageView>
      </div>
    );
  } else {
    return (
      <div className={cx(classes.pdfContainer, classes.iosErrorContainer)}>
        {props.documents.length > 1 && (
          <>
            <ZoomableImageNavigation
              direction="prev"
              imageCount={props.documents.length}
              imageIndex={documentIndex}
              onClick={() => setDocumentIndex(Math.max(documentIndex - 1, 0))}
              header={capitalize(caption)}
              headerEttGenus
            />
            <ZoomableImageNavigation
              direction="next"
              imageCount={props.documents.length}
              imageIndex={documentIndex}
              onClick={() =>
                setDocumentIndex(
                  Math.min(documentIndex + 1, props.documents.length - 1)
                )
              }
              header={capitalize(caption)}
              headerEttGenus
            />
            <ZoomableImageHeader
              count={props.documents.length}
              index={documentIndex}
              header="Underlag"
            />
          </>
        )}
        <EkonomiskAnalysDataErrorPage
          overrideIconName="description"
          errorText={`Filformatet för filen ${props.documents[documentIndex].fileName}.${document.extension} stöds ej att visa direkt eller skriva ut`}
          buttonText="Klicka här för att ladda ner filen"
          onButtonClick={() => {
            downloadDocument(
              document.fileName,
              document.url,
              document.extension,
              props.appendDownloadedDateToFileName
            );
          }}
        />
      </div>
    );
  }
};

export default Documents;
